import '../title';
import '../text';
import { defineCustomElement, getBooleanAttribute, getAttribute, updateAttribute, setClass, NectaryElement, isAttrTrue, getRect, isAttrEqual, updateBooleanAttribute } from '../utils';
const templateHTML = '<style>:host{display:block;outline:0}#wrapper{border-radius:var(--sinch-comp-card-shape-radius);border:1px solid var(--sinch-comp-card-color-white-default-border-initial);background-color:var(--sinch-comp-card-color-white-default-background-initial);box-shadow:var(--sinch-comp-card-shadow-initial);overflow:hidden}:host(:hover) #wrapper{box-shadow:var(--sinch-comp-card-shadow-hover)}#card-body{padding:24px;box-sizing:border-box;display:flex;flex-direction:column;gap:16px}#illustration-wrapper{display:none;overflow:hidden;height:240px}#illustration-wrapper.active{display:block}#label{--sinch-comp-text-font:var(--sinch-comp-card-font-label);--sinch-global-color-text:var(--sinch-comp-card-color-white-default-title-initial)}#label:empty{display:none}#header{display:flex;flex-direction:row;align-items:center;gap:8px;--sinch-global-size-icon:var(--sinch-comp-card-size-icon)}#caption{flex:1;min-width:0;--sinch-comp-title-font:var(--sinch-comp-card-font-title);--sinch-global-color-text:var(--sinch-comp-card-color-white-default-title-initial)}#description{flex:1;min-height:0;max-height:120px;overflow-y:auto;--sinch-comp-text-font:var(--sinch-comp-card-font-description);--sinch-global-color-text:var(--sinch-comp-card-color-white-default-description-initial)}#description:empty{display:none}slot[name=action]::slotted(*){margin-top:20px;align-self:flex-start;max-width:100%}:host([draggable=true]) #header{cursor:grab}</style><div id="wrapper"><div id="illustration-wrapper"><slot name="illustration"></slot></div><div id="card-body"><sinch-text id="label" type="s" emphasized ellipsis></sinch-text><div id="header"><slot name="icon"></slot><sinch-title id="caption" type="m" level="3" ellipsis></sinch-title></div><sinch-text id="description" type="m"></sinch-text><slot name="action"></slot></div></div>';
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-card', class extends NectaryElement {
  #$text;
  #$label;
  #$caption;
  #$header;
  #$cardBody;
  #$iconSlot;
  #$illustrationSlot;
  #$illustrationSlotWrapper;
  #isDraggingCorrectHandle = false;
  #isDraggingSubscribed = false;
  #controller = null;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$text = shadowRoot.querySelector('#description');
    this.#$label = shadowRoot.querySelector('#label');
    this.#$caption = shadowRoot.querySelector('#caption');
    this.#$header = shadowRoot.querySelector('#header');
    this.#$cardBody = shadowRoot.querySelector('#wrapper');
    this.#$iconSlot = shadowRoot.querySelector('slot[name="icon"]');
    this.#$illustrationSlot = shadowRoot.querySelector('slot[name="illustration"]');
    this.#$illustrationSlotWrapper = shadowRoot.querySelector('#illustration-wrapper');
  }
  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const options = {
      signal: this.#controller.signal
    };
    this.#$illustrationSlot.addEventListener('slotchange', this.#onIllustrationSlotChange, options);
    this.#onIllustrationSlotChange();
    if (getBooleanAttribute(this, 'draggable')) {
      this.#enableDraggable();
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#disableDragging();
    this.#controller.abort();
    this.#controller = null;
  }
  static get observedAttributes() {
    return ['text', 'label', 'caption', 'draggable'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'text':
        {
          this.#$text.textContent = newVal;
          break;
        }
      case 'label':
        {
          this.#$label.textContent = newVal;
          break;
        }
      case 'caption':
        {
          updateAttribute(this.#$caption, 'text', newVal);
          break;
        }
      case 'draggable':
        {
          if (isAttrEqual(oldVal, newVal)) {
            return;
          }
          const isDraggingEnabled = isAttrTrue(newVal);
          if (isDraggingEnabled) {
            this.#enableDraggable();
          } else {
            this.#disableDragging();
          }
          updateBooleanAttribute(this, name, isDraggingEnabled);
          break;
        }
    }
  }
  set text(value) {
    updateAttribute(this, 'text', value);
  }
  get text() {
    return getAttribute(this, 'text', '');
  }
  set caption(value) {
    updateAttribute(this, 'caption', value);
  }
  get caption() {
    return getAttribute(this, 'caption', '');
  }
  set label(value) {
    updateAttribute(this, 'label', value);
  }
  get label() {
    return getAttribute(this, 'label', '');
  }
  get dragRect() {
    return this.#isDraggingSubscribed ? getRect(this.#$header) : null;
  }
  #onIllustrationSlotChange = () => {
    setClass(this.#$illustrationSlotWrapper, 'active', this.#$illustrationSlot.assignedElements().length > 0);
  };
  #enableDraggable() {
    if (this.isDomConnected && !this.#isDraggingSubscribed) {
      this.addEventListener('dragstart', this.#onDragStart);
      this.#$cardBody.addEventListener('mousedown', this.#onDraggableMouseDown);
      this.#isDraggingSubscribed = true;
    }
  }
  #disableDragging() {
    if (this.#isDraggingSubscribed) {
      this.removeEventListener('dragstart', this.#onDragStart);
      this.#$cardBody.removeEventListener('mousedown', this.#onDraggableMouseDown);
      this.#isDraggingSubscribed = false;
    }
  }
  #onDraggableMouseDown = e => {
    this.#isDraggingCorrectHandle = this.#$header.contains(e.target) || this.#$iconSlot.assignedElements().includes(e.target);
  };
  #onDragStart = e => {
    if (!this.#isDraggingCorrectHandle) {
      e.preventDefault();
      e.stopPropagation();
    }
  };
});